import React, { useMemo } from 'react';
import { MultiSelect, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { MenuItem, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';

interface UserProfile {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
  name?: string;
}

interface TeamMemberMultiSelectProps {
  selectedUserIds: string[];
  onUserIdsChange: (userIds: string[]) => void;
  placeholder?: string;
  tagMinimal?: boolean;
}

// Fuzzy search predicate - searches by name and email
const filterUser: ItemPredicate<UserProfile> = (query, user, _index, exactMatch) => {
  const normalizedQuery = query.toLowerCase();
  const normalizedName = (user.name || '').toLowerCase();
  const normalizedEmail = (user.email || '').toLowerCase();
  const normalizedFirstName = (user.firstName || '').toLowerCase();
  const normalizedLastName = (user.lastName || '').toLowerCase();

  if (exactMatch) {
    return normalizedName === normalizedQuery || normalizedEmail === normalizedQuery;
  }

  // Check if query matches any part of name, first name, last name, or email
  return (
    normalizedName.indexOf(normalizedQuery) >= 0 ||
    normalizedEmail.indexOf(normalizedQuery) >= 0 ||
    normalizedFirstName.indexOf(normalizedQuery) >= 0 ||
    normalizedLastName.indexOf(normalizedQuery) >= 0
  );
};

const TeamMemberMultiSelect: React.FC<TeamMemberMultiSelectProps> = observer(({
  selectedUserIds,
  onUserIdsChange,
  placeholder = 'Select team members...',
  tagMinimal = false
}) => {
  const store = useAppStore();

  // Get users from the UserStore
  const userProfiles = useMemo(() => {
    if (!store) {
      return [];
    }
    return store.user.users.map(user => ({
      id: user.id,
      userId: user.id,
      firstName: user.firstName || user.name?.split(' ')[0],
      lastName: user.lastName || user.name?.split(' ')[1],
      email: user.email,
      avatar: user.avatarUrl || user.avatar,
      name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
    }));
  }, [store?.user.users]);

  const getDisplayName = (profile: UserProfile) => {
    if (profile.firstName || profile.lastName) {
      return `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    }
    return profile.email || 'Unknown User';
  };

  const getInitials = (profile: UserProfile) => {
    const name = getDisplayName(profile);
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Render individual item in dropdown
  const renderUser: ItemRenderer<UserProfile> = (user, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    const isSelected = selectedUserIds.includes(user.userId);

    return (
      <MenuItem
        key={user.userId}
        text={getDisplayName(user)}
        label={user.email}
        icon={isSelected ? IconNames.TICK : IconNames.PERSON}
        onClick={handleClick}
        active={modifiers.active}
        disabled={modifiers.disabled}
      />
    );
  };

  // Render selected user as tag
  const renderTag = (userId: string) => {
    const user = userProfiles.find(u => u.userId === userId);
    if (!user) return userId;

    return getDisplayName(user);
  };

  // Handle selection
  const handleItemSelect = (user: UserProfile) => {
    if (selectedUserIds.includes(user.userId)) {
      // Remove user
      onUserIdsChange(selectedUserIds.filter(id => id !== user.userId));
    } else {
      // Add user
      onUserIdsChange([...selectedUserIds, user.userId]);
    }
  };

  // Handle tag remove
  const handleTagRemove = (_tag: React.ReactNode, index: number) => {
    const newUserIds = [...selectedUserIds];
    newUserIds.splice(index, 1);
    onUserIdsChange(newUserIds);
  };

  // Clear all
  const handleClear = () => {
    onUserIdsChange([]);
  };

  return (
    <MultiSelect<UserProfile>
      items={userProfiles}
      selectedItems={userProfiles.filter(u => selectedUserIds.includes(u.userId))}
      onItemSelect={handleItemSelect}
      itemRenderer={renderUser}
      itemPredicate={filterUser}
      tagRenderer={renderTag}
      onRemove={handleTagRemove}
      onClear={handleClear}
      placeholder={placeholder}
      resetOnSelect={true}
      fill={true}
      popoverProps={{
        minimal: true,
        matchTargetWidth: true,
      }}
      tagInputProps={{
        tagProps: { minimal: tagMinimal },
        onRemove: handleTagRemove,
        rightElement: selectedUserIds.length > 0 ? (
          <Tag minimal interactive onClick={handleClear} style={{ cursor: 'pointer' }}>
            Clear
          </Tag>
        ) : undefined,
      }}
    />
  );
});

export default TeamMemberMultiSelect;

